import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styled from '../../utils/styled';
import AvatarImage from './private/AvatarImage';
import AvatarText from './private/AvatarText';
import {AvatarProps, AvatarState} from './types';

interface RootProps extends Pick<AvatarProps, 'circle' | 'src' | 'small' | 'large' | 'larger'> {}

const Root = styled('div')<RootProps>(
  ({circle, src, theme}) => ({
    fontFamily: theme.fontFamily.base,
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'inline-block',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    position: 'relative',
    overflow: 'hidden',
    verticalAlign: 'middle',
    lineHeight: '32px',
    borderRadius: circle ? '50%' : 4,
    background: src ? undefined : '#ddd',
    color: '#fff'
  }),
  ({small, large, larger}) => {
    if (larger) {
      return {
        fontSize: 18,
        width: 64,
        height: 64,
        lineHeight: '64px'
      };
    }
    if (large) {
      return {
        fontSize: 18,
        width: 44,
        height: 44,
        lineHeight: '44px'
      };
    }
    if (small) {
      return {
        fontSize: 14,
        width: 24,
        height: 24,
        lineHeight: '24px'
      };
    }
    return {
      fontSize: 14,
      width: 32,
      height: 32,
      lineHeight: '32px'
    };
  }
);

export default class Avatar extends React.Component<AvatarProps, AvatarState> {
  private _textContent: HTMLElement;

  constructor(props: AvatarProps) {
    super(props);
    this.state = {
      textScale: 1
    };
  }

  public componentDidUpdate(prevProps: AvatarProps) {
    if (prevProps.children !== this.props.children) {
      this._setTextScale();
    }
  }

  public render() {
    return <Root {...this.props}>{this._renderContent()}</Root>;
  }

  private _renderContent() {
    const {icon, src, alt, children} = this.props;

    if (icon) {
      return icon;
    }

    if (src) {
      return <AvatarImage src={src} alt={alt} />;
    }

    const textContent = <AvatarText innerRef={this._setTextNode}>{children}</AvatarText>;
    const scale = this.state.textScale;

    if (this._textContent && scale !== 1) {
      const textContentStyles: React.CSSProperties = {
        msTransform: `scale(${scale})`,
        WebkitTransform: `scale(${scale})`,
        transform: `scale(${scale})`,
        position: 'absolute',
        display: 'inline-block',
        left: `calc(50% - ${Math.round(this._textContent!.offsetWidth / 2)}px)`
      };

      return React.cloneElement(textContent, {style: textContentStyles});
    }
    return textContent;
  }

  private _setTextScale() {
    const textNode = this._textContent;

    if (textNode) {
      const textWidth = textNode.offsetWidth;
      const rootNode = ReactDOM.findDOMNode(this) as Element;
      const rootWidth = rootNode.getBoundingClientRect().width;

      // Give the avatar 4px * 2 horizontal padding
      if (rootWidth - 8 < textWidth) {
        this.setState({
          textScale: (rootWidth - 8) / textWidth
        });
      } else {
        this.setState({
          textScale: 1
        });
      }
    }
  }

  private _setTextNode = (node: HTMLElement) => {
    this._textContent = node;
    this._setTextScale();
  };
}



// WEBPACK FOOTER //
// ./src/components/Avatar/Avatar.tsx